import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { Workspace } from "./types";
import { NavIcons } from "./icons";

/**
 * WorkspaceSwitcher
 * ─────────────────────────────────────────────────────────────
 * Sits at the top of both rails. Two visual modes:
 *
 *   • "rail-slim"  — collapsed pill, shows only the workspace mark.
 *                    Used in the customer CRM (56px rail) and in the
 *                    collapsed operator rail.
 *
 *   • "rail-wide"  — pill with workspace name + chevron.
 *                    Used in the expanded operator rail.
 *
 * If the user only has one workspace (the common customer-user case),
 * the pill is rendered non-interactive — no chevron, no popover.
 *
 * The popover is intentionally minimal: a tiny search, "Operator
 * Console" pinned at the top for internal staff, then a list of every
 * customer workspace the user can enter. Selecting one calls
 * `onSelect(workspaceId)` — the caller decides whether that means
 * "navigate" or "begin impersonation".
 */

export interface WorkspaceSwitcherProps {
  /** Every workspace this user is allowed to access. */
  workspaces: Workspace[];
  /** Currently-active workspace id. */
  activeId: string;
  /** Visual variant. Defaults to "rail-slim". */
  variant?: "rail-slim" | "rail-wide";
  /** True if this user is impersonating someone (shows badge + tint). */
  isImpersonating?: boolean;
  /** Called when the user picks a workspace. */
  onSelect?: (workspaceId: string) => void;
}

export function WorkspaceSwitcher({
  workspaces,
  activeId,
  variant = "rail-slim",
  isImpersonating = false,
  onSelect,
}: WorkspaceSwitcherProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const active = workspaces.find((w) => w.id === activeId) ?? workspaces[0];
  const isSingle = workspaces.length <= 1;

  // Click-outside + Escape to dismiss.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!active) return null;

  // Group the popover list: Operator first (if present), then customers.
  const operator = workspaces.find((w) => w.kind === "operator");
  const customers = workspaces.filter((w) => w.kind === "customer");

  const pickWorkspace = (id: string) => {
    setOpen(false);
    onSelect?.(id);
  };

  return (
    <div
      ref={rootRef}
      className={
        "rr-ws" +
        ` rr-ws--${variant}` +
        (isImpersonating ? " is-impersonating" : "") +
        (open ? " is-open" : "")
      }
    >
      <button
        type="button"
        className="rr-ws-trigger"
        aria-haspopup={isSingle ? undefined : "listbox"}
        aria-expanded={isSingle ? undefined : open}
        aria-label={isSingle ? active.name : `${active.name} — switch workspace`}
        disabled={isSingle}
        onClick={() => !isSingle && setOpen((o) => !o)}
      >
        <WorkspaceMark ws={active} />
        {variant === "rail-wide" && (
          <>
            <span className="rr-ws-label">
              <span className="rr-ws-label-name">{active.name}</span>
              <span className="rr-ws-label-sub">
                {isImpersonating ? "Impersonating" : active.kind === "operator" ? "Operator console" : "Workspace"}
              </span>
            </span>
            {!isSingle && <span className="rr-ws-chev">{NavIcons.chevronDown}</span>}
          </>
        )}
      </button>

      {open && !isSingle && (
        <Popover
          operator={operator}
          customers={customers}
          activeId={active.id}
          onPick={pickWorkspace}
          anchor={variant}
        />
      )}
    </div>
  );
}

/* ─── Internal ─── */

function WorkspaceMark({ ws }: { ws: Workspace }) {
  // When the workspace has an image, drop the colored tile and let the
  // logo silhouette sit directly on the rail background. Without an
  // image we keep the colored mark + initials. Class-based gate (rather
  // than CSS `:has(img)`) so it works in every browser engine.
  const hasLogo = !!ws.logoUrl;
  const style: CSSProperties = hasLogo
    ? {}
    : { background: ws.color ?? "var(--rr-deep)" };
  return (
    <span className={"rr-ws-mark" + (hasLogo ? " has-logo" : "")} style={style}>
      {hasLogo
        ? (
          <img
            src={ws.logoUrl}
            alt=""
            // Surface load failures directly in DevTools so the brand
            // mark never silently looks broken.
            onError={(e) => {
              // eslint-disable-next-line no-console
              console.error("WorkspaceMark: logo failed to load", ws.logoUrl, e);
            }}
          />
        )
        : <span>{ws.initials}</span>
      }
    </span>
  );
}

function Popover({
  operator,
  customers,
  activeId,
  onPick,
  anchor,
}: {
  operator?: Workspace;
  customers: Workspace[];
  activeId: string;
  onPick: (id: string) => void;
  anchor: "rail-slim" | "rail-wide";
}) {
  const [q, setQ] = useState("");
  const filtered = customers.filter(
    (w) =>
      !q ||
      w.name.toLowerCase().includes(q.toLowerCase()) ||
      (w.subtitle ?? "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className={`rr-ws-pop rr-ws-pop--${anchor}`} role="listbox">
      <div className="rr-ws-pop-search">
        <span aria-hidden="true">{NavIcons.search}</span>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search workspaces"
        />
      </div>

      {operator && !q && (
        <>
          <div className="rr-ws-pop-section">Internal</div>
          <WorkspaceRow ws={operator} active={operator.id === activeId} onClick={() => onPick(operator.id)} />
        </>
      )}

      {customers.length > 0 && (
        <>
          <div className="rr-ws-pop-section">
            Customer workspaces
            <span className="rr-ws-pop-count">{filtered.length}</span>
          </div>
          <div className="rr-ws-pop-scroll">
            {filtered.map((w) => (
              <WorkspaceRow key={w.id} ws={w} active={w.id === activeId} onClick={() => onPick(w.id)} />
            ))}
            {filtered.length === 0 && <div className="rr-ws-pop-empty">No matches</div>}
          </div>
        </>
      )}

      <div className="rr-ws-pop-foot">
        <kbd>⌘</kbd> <kbd>K</kbd>
        <span>to open switcher anywhere</span>
      </div>
    </div>
  );
}

function WorkspaceRow({
  ws,
  active,
  onClick,
}: {
  ws: Workspace;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      className={"rr-ws-pop-row" + (active ? " is-active" : "")}
      onClick={onClick}
    >
      <WorkspaceMark ws={ws} />
      <span className="rr-ws-pop-row-text">
        <span className="rr-ws-pop-row-name">{ws.name}</span>
        {ws.subtitle && <span className="rr-ws-pop-row-sub">{ws.subtitle}</span>}
      </span>
      {active && <span className="rr-ws-pop-row-check">{NavIcons.check}</span>}
    </button>
  );
}
